import { useEffect, useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { fetchLeverStages, type LeverStage } from '../../services/leverApi';
import { isBoardScheduledStage } from './boardScheduledStages';

/**
 * Interview stage, chosen from Lever's own pipeline.
 *
 * Not a text field: a typed stage cannot be joined to a Lever candidate, whose
 * stage arrives as an id (e.g. c59740e3-… for "First Interview"). Selecting from
 * Lever stores both the id and the canonical name, so the automation matches
 * exactly and the candidate sees Lever's wording.
 *
 * Only actual interview stages are offered — an interview link is for booking an
 * interview, so lead/applicant/shortlist/offer stages are filtered out.
 */
export function StageSelect({
  stageId,
  onChange,
}: {
  stageId: string;
  onChange: (stage: { stageId: string; stageName: string }) => void;
}) {
  const [stages, setStages] = useState<LeverStage[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchLeverStages().then((res) => {
      if (cancelled) return;
      setStages(res.stages);
      setUnavailable(!res.available);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only the opening rounds (First / Personal Interview) get manual booking links
  // here. Case interviews and final rounds (incl. Bar Raiser) are scheduled in the
  // Action Board directly, so they are excluded — a link here would be redundant
  // and confusing (see isBoardScheduledStage). A currently-stored non-interview
  // stage is still kept so an old link keeps showing its stage.
  const interviewStages = useMemo(
    () =>
      stages.filter(
        (s) => !isBoardScheduledStage(s.name) && (/interview/i.test(s.name) || s.id === stageId)
      ),
    [stages, stageId]
  );

  const disabled = !loaded || interviewStages.length === 0;

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">
        Interview stage <span className="text-red-600">*</span>
      </span>
      <Select
        value={stageId || undefined}
        disabled={disabled}
        onValueChange={(v) => {
          const picked = interviewStages.find((s) => s.id === v);
          onChange({ stageId: picked?.id || '', stageName: picked?.name || '' });
        }}
      >
        <SelectTrigger className="w-full rounded-xl">
          <SelectValue placeholder={loaded ? 'Choose a stage' : 'Loading stages…'} />
        </SelectTrigger>
        <SelectContent>
          {interviewStages.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {unavailable ? (
        <span className="mt-1 block text-xs text-[#854F0B]">
          Could not load stages from Lever — a stage must be chosen, so saving is blocked until the
          lookup works.
        </span>
      ) : (
        <span className="mt-1 block text-xs text-gray-500">
          Lever’s interview stages. The candidate sees this wording.
        </span>
      )}
    </label>
  );
}
